import * as yup from 'yup';
import { gql } from '__generated__';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetOrganisationForProjectQuery as GetOrganisationForProject } from '__generated__/graphql';

import { ImageInput } from '../../common/Common/UploadButton';
import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import UI from '../../common/UI';
import useCountries from '../../common/useCountries';
import useCurrencies from '../../common/useCurrencies';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useNavigationPrompt from '../../common/useNavigationPrompt';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

export const EditOrganisationMutation = gql(`
  mutation EditOrganisation($input: EditOrganisationInput!) {
    editOrganisation(input: $input) {
      ...ProjectFields
    }
  }
`);

const editOrganisationSchema = yup.object().shape({
  organisation_name: yup.string().required(),
  organisation_address: yup.string().ensure(),
  organisation_zip_code: yup.string().ensure(),
  organisation_country: yup.string().required(),
  organisation_vat_number: yup.string().ensure(),
  description: yup.string().ensure(),
  locale: yup.string().required(),
  timezone: yup.string().required(),
});

const getValues = (project: GetOrganisationForProject['project']) => ({
  organisation_name: project.organisation_name || '',
  organisation_email: project.organisation_email || '',
  organisation_address: project.organisation_address || '',
  organisation_zip_code: project.organisation_zip_code || '',
  organisation_city: project.organisation_city || '',
  organisation_country: project.organisation_country || '',
  organisation_vat_number: project.organisation_vat_number || '',
  organisation_registration_number: project.organisation_registration_number || '',
  logo: null as File | null,
  delete_logo: false,
  currency: project.currency,
  shift_vat: project.shift_vat || false,
  locale: project.locale,
  timezone: project.timezone,
});

export const GetOrganisationForProjectQuery = gql(`
  query GetOrganisationForProject($id: ID!) {
    project(id: $id) {
      ...ProjectFields
    }
    timezones {
      name
      title
    }
  }
`);

interface EditOrganisationPageProps {
  projectId: string;
}

const EditOrganisationPage = ({ projectId }: EditOrganisationPageProps) => {
  const { data } = useQuery(GetOrganisationForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  if (!data) {
    return null;
  }

  return <EditOrganisationForm {...data} />;
};

const EditOrganisationForm = ({ project, timezones }: GetOrganisationForProject) => {
  const { t } = useTranslation();
  const { scrollTo } = useScroll();
  const { sortedLocales } = useLocale();
  const notifier = useNotifier();

  const form = useForm({
    values: getValues(project),
  });

  useNavigationPrompt(form.changed());

  const [editOrganisation, { error, loading }] = useMutation(
    EditOrganisationMutation, {
      variables: {
        input: {
          ...form.values,
          project: {
            id: project.id,
          },
        },
      },
      onCompleted: ({ editOrganisation: project }) => {
        notifier.success(t('notifications.project_saved'));

        form.reset(getValues(project));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const { countries } = useCountries();
  const { currencies } = useCurrencies();

  const { valid, errors } = useValidation({
    schema: editOrganisationSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H3>
            {t('organisation')}
          </UI.H3>
          <UI.InputDescription sc={{ mt: 1 }}>
            {t('organisation_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Form onSubmit={() => editOrganisation()}>
          <UI.Card>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.InputGroup sc={{ valid: !errors.organisation_name, touched: form.touched.organisation_name }}>
                <UI.InputLabel htmlFor="OrganisationName">
                  {t('organisation_name')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.DebouncedInput
                  {...form.text('organisation_name')}
                  sc={{ size: 'lg' }}
                  maxLength={50}
                  id="OrganisationName"
                />
                <UI.ErrorMessages
                  attribute={t('organisation_name')}
                  errors={errors.organisation_name}
                />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.logo, touched: form.touched.logo }} style={{ maxWidth: '100%' }}>
                <UI.InputLabel>
                  {t('logo')}
                  <UI.Label sc={{ brand: 'secondary', small: true, ml: 1, fontWeight: 500 }}>
                    {t('new')}
                  </UI.Label>
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('logo_description')}
                </UI.InputDescription>
                <ImageInput
                  value={form.values.logo}
                  deleteImage={form.values.delete_logo}
                  onChange={(file) => {
                    form.set('logo', file);
                    form.set('delete_logo', !file);
                  }}
                  previewImage={project.logo?.url}
                  imageAlt={project.organisation_name}
                  width={200}
                />
                <UI.ErrorMessages attribute={t('logo')} errors={errors.logo} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.organisation_email, touched: form.touched.organisation_email }}>
                <UI.InputLabel htmlFor="OrganisationEmailAddress">
                  {t('email_address')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('organisation_email_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('organisation_email')}
                  maxLength={255}
                  id="OrganisationEmailAddress"
                />
                <UI.ErrorMessages attribute={t('email_address')} errors={errors.organisation_email} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.organisation_address, touched: form.touched.organisation_address }}>
                <UI.InputLabel htmlFor="OrganisationAddress">
                  {t('address')}
                </UI.InputLabel>
                <UI.DebouncedInput
                  {...form.text('organisation_address')}
                  maxLength={255}
                  id="OrganisationAddress"
                />
                <UI.ErrorMessages attribute={t('address')} errors={errors.organisation_address} />
              </UI.InputGroup>

              <UI.GridContainer sc={{ columns: '1fr 1fr' }}>
                <UI.InputGroup
                  sc={{ valid: !errors.organisation_zip_code, touched: form.touched.organisation_zip_code }}
                >
                  <UI.InputLabel htmlFor="OrganisationZip_ode">
                    {t('zip_code')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    {...form.text('organisation_zip_code')}
                    maxLength={255}
                    id="OrganisationZipCode"
                  />
                  <UI.ErrorMessages attribute={t('zip_code')} errors={errors.organisation_address} />
                </UI.InputGroup>

                <UI.InputGroup sc={{ valid: !errors.organisation_city, touched: form.touched.organisation_city }}>
                  <UI.InputLabel htmlFor="OrganisationCity">
                    {t('city')}
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    {...form.text('organisation_city')}
                    maxLength={255}
                    id="OrganisationCity"
                  />
                  <UI.ErrorMessages attribute={t('city')} errors={errors.organisation_city} />
                </UI.InputGroup>
              </UI.GridContainer>

              <UI.InputGroup sc={{ valid: !errors.organisation_country, touched: form.touched.organisation_country }}>
                <UI.InputLabel htmlFor="OrganisationCountry">
                  {t('country')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.Select {...form.select('organisation_country', [null, ...countries])} id="OrganisationCountry">
                  <option value={0}> </option>
                  {countries.map((country, index) => (
                    <option {...form.option(index + 1)} key={index + 1}>{t(`common:countries.${country}`)}</option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('country')} errors={errors.organisation_country} />
              </UI.InputGroup>

              <UI.InputGroup
                sc={{
                  valid: !errors.organisation_registration_number,
                  touched: form.touched.organisation_registration_number,
                }}
              >
                <UI.InputLabel htmlFor="OrganisationRegistrationNumber">
                  {t('business_registration_number')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('business_registration_number_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('organisation_registration_number')}
                  maxLength={255}
                  id="OrganisationRegistrationNumber"
                />
                <UI.ErrorMessages
                  attribute={t('business_registration_number')}
                  errors={errors.organisation_registration_number}
                />
              </UI.InputGroup>

              <UI.InputGroup
                sc={{ valid: !errors.organisation_vat_number, touched: form.touched.organisation_vat_number }}
              >
                <UI.InputLabel htmlFor="OrganisationVatNumber">
                  {t('vat_number')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('vat_number_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('organisation_vat_number')}
                  maxLength={255}
                  id="OrganisationVatNumber"
                />
                <UI.ErrorMessages
                  attribute={t('vat_number')}
                  errors={errors.organisation_vat_number}
                />
              </UI.InputGroup>

              {form.values.organisation_vat_number && (
                <UI.InputGroup>
                  <UI.Checkbox
                    checked={form.values.shift_vat}
                    onChange={() => form.set('shift_vat', !form.values.shift_vat)}
                  >
                    {t('enable_shift_vat')}
                  </UI.Checkbox>
                </UI.InputGroup>
              )}

              <UI.InputGroup sc={{ valid: !errors.currency, touched: form.touched.currency }}>
                <UI.InputLabel htmlFor="Currency">
                  {t('currency')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('currency_description')}
                </UI.InputDescription>
                <UI.Select
                  {...form.select('currency', currencies)}
                  disabled={!project.currency_editable}
                  id="Currency"
                >
                  {currencies.map((currency, index) => (
                    <option {...form.option(index)} key={index}>{t(`common:currencies.${currency}`)}</option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('currency')} errors={errors.currency} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.locale, touched: form.touched.locale }}>
                <UI.InputLabel htmlFor="Language">
                  {t('language')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('project_language_description')}
                </UI.InputDescription>
                <UI.Select {...form.select('locale', sortedLocales)} id="Language">
                  {sortedLocales.map((locale, index) => (
                    <option {...form.option(index)} key={index}>
                      {t(`common:locales.${locale}`)}
                    </option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('language')} errors={errors.locale} />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.timezone, touched: form.touched.timezone }}>
                <UI.InputLabel htmlFor="Timezone">
                  {t('timezone')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.Select
                  {...form.select('timezone', timezones.map((timezone) => timezone.name))}
                  id="Timezone"
                >
                  {timezones.map((timezone: any, index: number) => (
                    <option {...form.option(index)} key={index}>{timezone.title}</option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('timezone')} errors={errors.timezone} />
              </UI.InputGroup>

              <UI.HR />

              <UI.FormButtons
                form={form}
                submitLabel={t('save')}
                loading={loading}
                valid={valid}
              />
            </UI.FormGrid>
          </UI.Card>
        </UI.Form>
      </UI.GridContainer>
    </UI.Container>
  );
};

export default EditOrganisationPage;
